import { Divider } from "@mui/material";
import React from "react";
import Article from "../../components/Article";
import TextLink from "../../components/TextLink";
import githubOidcImage from "../../assets/images/github-oidc.png";

export const OidcInPipelinesGithubActionsDetails = {
  articleTitle: "Using OIDC in Pipelines with GitHub Actions",
  articleDate: "2025-11-29",
  articleAuthor: "Harrison Cannon",
  articleImage: githubOidcImage,
  articleBody: "Learn why OIDC should replace long-lived CI/CD credentials and how to implement it in GitHub Actions for secure AWS deployments.",
  articleSlug: "oidc-in-pipelines-github-actions"
};

function OidcInPipelinesGithubActions() {
  function ArticleContent() {
    return (
      <div>
        <h2>Background</h2>

        <p>
          CI/CD pipelines often need access to cloud resources so they can build, test and deploy your applications. Historically, this access was commonly provided using long-lived access keys stored as repository secrets.
        </p>

        <p>
          That approach works, but it increases risk. If a secret is exposed, it can be reused until it is rotated. This is why many teams now use OpenID Connect (OIDC) federation, which allows pipelines to request short-lived credentials at runtime.
        </p>

        <Divider className="divider" />

        <h2>Why OIDC in pipelines is important</h2>

        <p>
          OIDC significantly improves your security posture by removing static cloud credentials from source control systems and secret stores.
        </p>

        <ul>
          <li>Short-lived credentials reduce blast radius compared to long-lived keys.</li>
          <li>No manual key rotation burden for pipeline identities.</li>
          <li>Trust policies can restrict which repositories, branches and environments can assume roles.</li>
          <li>Auditing is clearer because each role assumption is tied to workload identity claims.</li>
        </ul>

        <p>
          If you deploy to AWS from GitHub, using OIDC with IAM roles is now considered a best practice. See <TextLink url="https://docs.github.com/en/actions/deployment/security-hardening-your-deployments/about-security-hardening-with-openid-connect" text="GitHub OIDC security guidance" /> and <TextLink url="https://docs.aws.amazon.com/IAM/latest/UserGuide/id_roles_providers_create_oidc.html" text="AWS IAM OIDC provider documentation" />.
        </p>

        <Divider className="divider" />

        <h2>How the flow works</h2>

        <p>
          At runtime, GitHub Actions requests an OIDC token from GitHub&apos;s identity provider. AWS IAM validates the token and its claims, then allows the workflow to assume a role with temporary credentials.
        </p>

        <ol>
          <li>Workflow runs on GitHub Actions with <code>id-token: write</code> permission.</li>
          <li>GitHub issues an OIDC JWT for that job.</li>
          <li>AWS STS validates the JWT against your configured IAM OIDC provider.</li>
          <li>STS returns temporary credentials for the trusted IAM role.</li>
          <li>Workflow uses those credentials to deploy infrastructure or application code.</li>
        </ol>

        <Divider className="divider" />

        <h2>AWS setup</h2>

        <h3>Create the OIDC provider</h3>

        <p>
          In IAM, create an OIDC provider for GitHub with this issuer URL:
        </p>

        <pre>
          <code>
            {"https://token.actions.githubusercontent.com"}
          </code>
        </pre>

        <p>
          Audience should be:
        </p>

        <pre>
          <code>
            {"sts.amazonaws.com"}
          </code>
        </pre>

        <h3>Create an IAM role for GitHub Actions</h3>

        <p>
          Next, create a role trusted by the GitHub OIDC provider. Restrict the <code>sub</code> claim so only your repository and branch can assume the role.
        </p>

        <pre>
          <code>
            {`{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Principal": {
        "Federated": "arn:aws:iam::123456789012:oidc-provider/token.actions.githubusercontent.com"
      },
      "Action": "sts:AssumeRoleWithWebIdentity",
      "Condition": {
        "StringEquals": {
          "token.actions.githubusercontent.com:aud": "sts.amazonaws.com"
        },
        "StringLike": {
          "token.actions.githubusercontent.com:sub": "repo:your-org/your-repo:ref:refs/heads/main"
        }
      }
    }
  ]
}`}
          </code>
        </pre>

        <p>
          Attach least-privilege permissions to this role based on what the pipeline must do, for example deploying CloudFormation, CDK, ECS, or S3 assets.
        </p>

        <Divider className="divider" />

        <h2>GitHub Actions example</h2>

        <p>
          The workflow below assumes the IAM role using OIDC and then runs deployment commands with temporary AWS credentials.
        </p>

        <pre>
          <code>
            {`name: Deploy

on:
  push:
    branches:
      - main

permissions:
  id-token: write
  contents: read

jobs:
  deploy:
    runs-on: ubuntu-latest
    steps:
      - name: Checkout
        uses: actions/checkout@v4

      - name: Configure AWS Credentials
        uses: aws-actions/configure-aws-credentials@v4
        with:
          role-to-assume: arn:aws:iam::123456789012:role/github-actions-deploy-role
          role-session-name: github-actions-deploy
          aws-region: eu-west-2

      - name: Verify identity
        run: aws sts get-caller-identity

      - name: Deploy
        run: |
          npm ci
          npm run build
          npm run deploy`}
          </code>
        </pre>

        <Divider className="divider" />

        <h2>Practical guardrails</h2>

        <p>
          For production use, harden both IAM and GitHub configuration so only trusted workflows can deploy.
        </p>

        <ul>
          <li>Scope trust to specific repositories, branches, tags, or GitHub environments.</li>
          <li>Use separate IAM roles per environment such as dev, staging and prod.</li>
          <li>Require pull request approvals and protected branches before deployment jobs run.</li>
          <li>Keep role permissions tightly scoped to the resources that pipeline needs.</li>
          <li>Monitor <code>AssumeRoleWithWebIdentity</code> calls in CloudTrail.</li>
        </ul>

        <Divider className="divider" />

        <h2>Final thoughts</h2>

        <p>
          OIDC in pipelines is one of the most impactful security improvements you can make with relatively low effort. You remove long-lived keys, reduce secret management overhead, and gain tighter control over who can deploy what.
        </p>

        <p>
          If you are still using static AWS credentials in CI/CD, this is a great migration to prioritise. Start with one repository and one deployment role, validate the trust boundaries, then roll the pattern out across your engineering teams.
        </p>
      </div>
    );
  }

  return <Article
    article={OidcInPipelinesGithubActionsDetails}
    content={ArticleContent}
  />;
}

export default OidcInPipelinesGithubActions;
